import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath } from '../../config/roleConfig';
import { Logo } from '../common';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';

const MfaChallengePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mfaToken, methods = [] } = location.state || {};

  const [activeMethod, setActiveMethod] = useState(methods[0] || 'totp');
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [webauthnOptions, setWebauthnOptions] = useState(null);
  const [challengeId, setChallengeId] = useState(null);

  const { completeLogin } = useAuth();
  const codeInputRef = useRef(null);

  // Redirect if no mfaToken
  useEffect(() => {
    if (!mfaToken) {
      navigate('/login', { replace: true });
    }
  }, [mfaToken, navigate]);

  // Fetch challenge on mount (for WebAuthn options)
  const fetchChallenge = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/mfa/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken }),
      });
      const data = await res.json();
      if (res.ok) {
        setWebauthnOptions(data.webauthnOptions);
        setChallengeId(data.challengeId);
      }
    } catch (err) {
      console.error('Failed to fetch MFA challenge:', err);
    }
  }, [mfaToken]);

  useEffect(() => {
    if (mfaToken && methods.includes('webauthn')) {
      fetchChallenge();
    }
  }, [mfaToken, methods, fetchChallenge]);

  // Focus code input
  useEffect(() => {
    if (activeMethod === 'totp' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [activeMethod]);

  const handleSuccess = (data) => {
    completeLogin(data);
    const dashboardPath = getDashboardPath(data.user.role);
    navigate(dashboardPath, { replace: true });
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || totpCode.length !== 6) return;
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, method: 'totp', code: totpCode }),
      });
      const data = await res.json();
      if (res.ok) {
        handleSuccess(data);
      } else {
        setError(data.error || 'Invalid code. Please try again.');
        setTotpCode('');
        setIsSubmitting(false);
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleWebauthnVerify = async () => {
    if (isSubmitting || !webauthnOptions) return;
    setIsSubmitting(true);
    setError('');

    try {
      const credential = await startAuthentication({ optionsJSON: webauthnOptions });
      const res = await fetch(`${API_URL}/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, method: 'webauthn', credential, challengeId }),
      });
      const data = await res.json();
      if (res.ok) {
        handleSuccess(data);
      } else {
        setError(data.error || 'WebAuthn verification failed.');
        setIsSubmitting(false);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Authentication was cancelled or timed out.');
      } else {
        setError('WebAuthn verification failed. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !recoveryCode.trim()) return;
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfaToken, method: 'recovery', code: recoveryCode.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        handleSuccess(data);
      } else {
        setError(data.error || 'Invalid recovery code.');
        setRecoveryCode('');
        setIsSubmitting(false);
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!mfaToken) return null;

  return (
    <div className="min-h-screen flex font-sans text-black antialiased bg-white">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#4D4D4D] relative overflow-hidden border-r border-[#4D4D4D]">
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <Logo variant="white" />
          </div>
          <div className="space-y-6 my-auto">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">Two-Factor Authentication</span>
            <h1 className="text-3xl lg:text-4xl font-bold font-sans text-white leading-tight">
              Verify your identity to continue to the platform.
            </h1>
          </div>
          <div className="pt-6 border-t border-white/20 flex items-center justify-between text-xs font-mono text-zinc-300">
            <span>&copy; 2026 Forwards Flow Inc.</span>
            <span>Secure authentication required.</span>
          </div>
        </div>
      </div>

      {/* Right Panel - MFA Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="max-w-md w-full">
          <div className="lg:hidden mb-8">
            <Logo />
          </div>

          <div className="mb-8">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">Security Verification</span>
            <h2 className="text-2xl font-bold font-sans text-black">Two-factor authentication</h2>
            <p className="text-xs font-mono text-zinc-500 mt-1">
              {showRecovery ? 'Enter a recovery code to access your account' : 'Verify your identity with a second factor'}
            </p>
          </div>

          <div className="border border-zinc-300 p-8 rounded-none bg-white">
            {error && (
              <div className="mb-6 p-4 bg-zinc-50 border border-zinc-300 rounded-none font-mono text-xs text-black">
                {error}
              </div>
            )}

            {/* Method Tabs */}
            {!showRecovery && methods.length > 1 && (
              <div className="flex gap-2 mb-6">
                {methods.includes('totp') && (
                  <button
                    onClick={() => { setActiveMethod('totp'); setError(''); }}
                    className={`flex-1 py-2 px-3 font-mono text-xs uppercase tracking-[0.15em] border transition-colors ${
                      activeMethod === 'totp' ? 'bg-[#4D4D4D] text-white border-[#4D4D4D]' : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400'
                    }`}
                  >
                    Authenticator
                  </button>
                )}
                {methods.includes('webauthn') && (
                  <button
                    onClick={() => { setActiveMethod('webauthn'); setError(''); }}
                    className={`flex-1 py-2 px-3 font-mono text-xs uppercase tracking-[0.15em] border transition-colors ${
                      activeMethod === 'webauthn' ? 'bg-[#4D4D4D] text-white border-[#4D4D4D]' : 'bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400'
                    }`}
                  >
                    Windows Hello
                  </button>
                )}
              </div>
            )}

            {/* TOTP Input */}
            {!showRecovery && activeMethod === 'totp' && (
              <form onSubmit={handleTotpSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Authentication Code
                  </label>
                  <input
                    ref={codeInputRef}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-zinc-300 rounded-none text-center text-lg font-mono tracking-[0.5em] text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors disabled:bg-zinc-100"
                    placeholder="000000"
                  />
                  <p className="mt-2 text-xs font-mono text-zinc-400">Enter the 6-digit code from your authenticator app</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || totpCode.length !== 6}
                  className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Verifying...</span>
                    </>
                  ) : 'Verify Code'}
                </button>
              </form>
            )}

            {/* WebAuthn */}
            {!showRecovery && activeMethod === 'webauthn' && (
              <div className="space-y-6">
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-4 bg-zinc-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                    </svg>
                  </div>
                  <p className="text-sm font-mono text-zinc-600 mb-1">Use your fingerprint or device PIN</p>
                  <p className="text-xs font-mono text-zinc-400">Click the button below to verify with Windows Hello</p>
                </div>
                <button
                  onClick={handleWebauthnVerify}
                  disabled={isSubmitting || !webauthnOptions}
                  className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Verifying...</span>
                    </>
                  ) : 'Verify with Windows Hello'}
                </button>
              </div>
            )}

            {/* Recovery Code */}
            {showRecovery && (
              <form onSubmit={handleRecoverySubmit} className="space-y-6">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">
                    Recovery Code
                  </label>
                  <input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-zinc-300 rounded-none text-center text-sm font-mono tracking-widest text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors disabled:bg-zinc-100"
                    placeholder="XXXX-XXXX"
                  />
                  <p className="mt-2 text-xs font-mono text-zinc-400">Enter one of your recovery codes</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || !recoveryCode.trim()}
                  className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? 'Verifying...' : 'Use Recovery Code'}
                </button>
              </form>
            )}

            {/* Toggle recovery / standard */}
            <div className="mt-6 pt-6 border-t border-zinc-200 text-center">
              <button
                onClick={() => { setShowRecovery(!showRecovery); setError(''); }}
                className="font-mono text-xs text-zinc-600 hover:text-black underline uppercase tracking-wider"
              >
                {showRecovery ? 'Use authenticator or Windows Hello instead' : 'Use a recovery code'}
              </button>
            </div>

            {/* Back to login */}
            <p className="mt-4 text-center text-xs font-mono text-zinc-400">
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="text-zinc-700 hover:text-black underline"
              >
                Back to sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaChallengePage;
